import toDate from 'date-fns/parse'
import parse from 'date-fns/parseISO'
import format from 'date-fns/format'
import { SchoolStudentDto } from '@ViewModels/SchoolStudentDto'
import { ensureValueIsDate } from './DateHelpers'

export const headerSpecs = [
   { name: 'NSN', alternatives: ['NationalSchoolNumber'], isNumber: true },
   { name: 'First Name', alternatives: ['Given Name'], required: true },
   { name: 'Last Name', alternatives: ['Surname', 'Family Name'], required: true },
   {
      name: 'Year',
      alternatives: ['Year Level', 'School Year', 'Year Level', 'Level'],
      isYear: true,
   },
   {
      name: 'DOB',
      alternatives: ['Date Of Birth', 'Birthdate', 'Birthday', 'Birth', 'Born'],
      isDate: true,
   },
   { name: 'Gender', alternatives: ['Sex'], isGender: true },
   { name: 'Ethnicity', alternatives: [] },
] as HeaderSpec[]

export const createHeaders = (parsedHeaders: string[], IGNORE: string) => {
   // Create a list of header data we will use to do the matching
   const headers: HeaderSpec[] = headerSpecs.map(h => {
      return {
         name: h.name,
         alternatives: h.alternatives,
         matched: false,
         matchedColumn: -1,
      }
   })
   const matchedHeaders: Header[] = []
   parsedHeaders.forEach((headerName, index) => {
      // Try to find a matching header in the ones we have so far not matched
      const unmatchedHeaders = headers.filter(h => {
         return !h.matched
      })
      const matchedColumn = matchHeader(headerName, unmatchedHeaders)
      const header: Header = { value: IGNORE, index }
      if (matchedColumn) {
         header.value = matchedColumn
      }
      matchedHeaders.push(header)
   })
   return matchedHeaders
}

const matchHeader = (name: string, unmatchedHeaders: HeaderSpec[]) => {
   for (let i = 0; i < unmatchedHeaders.length; i++) {
      const spec = unmatchedHeaders[i]
      const matched = testHeader(name, spec)
      if (matched) {
         spec.matched = true
         spec.matchedColumn = i
         return spec.name
      }
   }
}

const testHeader = (name: string, headerSpec: HeaderSpec) => {
   const tests = [headerSpec.name, ...headerSpec.alternatives]
   const matched = tests.some(test => {
      const match = stringMatch(name, test)
      return match
   })
   return matched
}

const removeEmptyRows = (data: string[][]) => {
   return data.filter(row => row.join('').trim() !== '')
}
export const pluralize = (value: number, singular: string, plural: string) => {
   if (value === 0) {
      return `No ${plural}`
   }
   if (value === 1) {
      return `${value} ${singular}`
   }
   return `${value} ${plural}`
}

const stringClean = (src: string) => {
   const split = src.toLowerCase().match(/\w/g)
   return split ? split.join('') : ''
}

const stringMatch = (source: string, target: string) => {
   source = stringClean(source)
   target = stringClean(target)
   if (source === target) {
      return true
   }
   if (source.indexOf(target) > -1) {
      return true
   }
   if (target.indexOf(source) > -1) {
      return true
   }
   return false
}

export const getValidationErrors = (
   headers: Header[],
   items: string[][],
   IGNORE: string
): string[] => {
   const errors = []

   if (!headers.some(h => h.value === 'First Name')) {
      errors.push('A First Name column is required')
   }
   if (!headers.some(h => h.value === 'Last Name')) {
      errors.push('A Last Name column is required')
   }

   const mappedHeaders = headers.filter(h => h.value !== IGNORE)
   mappedHeaders.forEach(h => {
      const others = mappedHeaders.filter(h2 => h.value === h2.value)
      if (others.length > 1) {
         errors.push('You can only have one column for ' + h.value)
      }
   })
   const mappedHeadersWithSpec = mappedHeaders.map(h =>
      Object.assign({ index: h.index }, headerSpecs.find(spec => h.value === spec.name))
   )

   const genderHeader = mappedHeadersWithSpec.find(h => h.isGender === true)
   if (genderHeader) {
      const genderColumn = items.map(row => row[genderHeader.index])
      const invalidGenders = genderColumn.filter(
         val => !['male', 'female', 'm', 'f', ''].includes(val.toLowerCase().trim())
      )

      if (invalidGenders.length > 0) {
         errors.push(
            pluralize(invalidGenders.length, 'row has', 'rows have') +
               ' an invalid value for Gender.  It must be one of male, female, m or f.'
         )
      }
   }

   const dateHeader = mappedHeadersWithSpec.find(h => h.isDate === true)
   if (dateHeader) {
      const dateColumn = items.map(row => row[dateHeader.index])
      const invalidDates = dateColumn.filter(dateString => {
         if (!dateString || dateString.trim() === '') {
            return false
         }
         return ['dd/MM/yyyy', 'd/M/yyyy'].every(formatString => {
            const date = toDate(dateString, formatString, new Date())
            return date.toString() === 'Invalid Date'
         })
      })
      if (invalidDates.length > 0) {
         errors.push(
            pluralize(invalidDates.length, 'row has', 'rows have') +
               ' an invalid value for Date Of Birth.  It must be a valid date in DD/MM/YYYY format or be empty.'
         )
      }
   }

   const requiredHeaders = mappedHeadersWithSpec.filter(h => h.required)
   requiredHeaders.forEach(requiredHeader => {
      const column = items.map(row => row[requiredHeader.index])
      const invalidRows = column.filter(val => !val || val.trim() === '')
      if (invalidRows.length > 0) {
         errors.push(
            pluralize(invalidRows.length, 'row is', 'rows are') +
               ' missing a value for ' +
               requiredHeader.name +
               '.  A value is required in this column.'
         )
      }
   })

   return errors.unique
}

export const getValidationWarnings = (
   headers: Header[],
   items: string[][],
   IGNORE: string
): string[] => {
   const warnings: string[] = []

   const mappedHeadersWithSpec = headers
      .filter(h => h.value !== IGNORE)
      .map(h => Object.assign({ index: h.index }, headerSpecs.find(spec => h.value === spec.name)))

   const numberHeaders = mappedHeadersWithSpec.filter(h => h.isNumber || h.isYear)

   numberHeaders.forEach(numberHeader => {
      const column = items.map(row => {
         return row[numberHeader.index]
      })
      const invalidRows = column.filter(val => {
         if (!val || val.trim() === '') {
            return false
         }
         return !/\d/.test(val)
      })
      if (invalidRows.length > 0) {
         warnings.push(
            pluralize(invalidRows.length, 'row does', 'rows do') +
               ' not have any numbers in the ' +
               numberHeader.name +
               ' column. '
         )
      }
   })

   return warnings.unique
}

export const generateCsv = (dtos: SchoolStudentDto[], ethnicityOptions: NameValuePair[]): any[] => {
   const items = dtos.map((student: SchoolStudentDto) => {
      const data = {
         NSN: '',
         FirstName: student.FirstName,
         LastName: student.LastName,
         Year: '',
         DOB: student.DateOfBirth
            ? format(ensureValueIsDate(student.DateOfBirth), 'dd/MM/yyyy')
            : '',
         Gender: student.Gender,
         Ethnicity: '',
         Class: student.ClassRoomNames,
         Target: student.IsTarget ? 'Yes' : 'No',
      }

      const yearReferenceType = student.ReferenceValues.find(ref => ref.Name === 'Year Level')
      if (yearReferenceType) {
         data.Year = yearReferenceType.Value
      }

      const nsnReferenceType = student.ReferenceValues.find(ref => ref.Name === 'NSN')
      if (nsnReferenceType) {
         data.NSN = nsnReferenceType.Value
      }

      const ethnicityReferenceType = student.ReferenceValues.find(ref => ref.Name === 'Ethnicity')

      if (ethnicityReferenceType) {
         const ethnicityId = ethnicityReferenceType.Value
         const ethnicity = ethnicityOptions.find(eth => eth.value === ethnicityId)
         if (ethnicity) {
            data.Ethnicity = ethnicity.name
         }
      }

      if (student.IsTarget) {
         const targetTypes = student.TargetTypes.map(type => type.Name)
         if (targetTypes.length) {
            data.Target += ' (' + targetTypes.join(', ') + ')'
         }
      }
      return data
   })
   return items
}

export interface HeaderSpec {
   name: string
   alternatives: string[]
   isNumber?: boolean
   required?: boolean
   isYear?: boolean
   isDate?: boolean
   isGender?: boolean
   matched?: boolean
   matchedColumn: number
}

export interface Header {
   value: string
   index: number
}

export const download = (filename: string, text: string) => {
   const element = document.createElement('a')
   element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text))
   element.setAttribute('download', filename)

   element.style.display = 'none'
   document.body.appendChild(element)

   element.click()
   document.body.removeChild(element)
}
